import { BookCallButton } from "@/components/shared/BookCallButton";
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  MapPin, Sparkles, ArrowRight, CheckCircle, Phone, Mail, Loader2, Building2,
} from "lucide-react";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { SEOExpansion } from "@/components/shared/SEOExpansion";
import { AwardsSection } from "@/components/shared/AwardsSection";
import { WhyChoosePmw } from "@/components/shared/WhyChoosePmw";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CityPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [city, setCity] = useState(null);
  const [otherCities, setOtherCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      axios.get(`${API_URL}/api/cities/${citySlug}`).then((r) => r.data).catch(() => null),
      axios.get(`${API_URL}/api/cities`).then((r) => r.data).catch(() => []),
    ]).then(([c, list]) => {
      if (cancelled) return;
      if (!c) { setNotFound(true); setLoading(false); return; }
      setCity(c);
      setOtherCities((list || []).filter((x) => x.slug !== c.slug && x.state_code === c.state_code).slice(0, 6));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [citySlug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-7 h-7 text-teal animate-spin" />
        </div>
      </PublicLayout>
    );
  }
  if (notFound || !city) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading text-3xl font-bold text-navy mb-3">City not found</h1>
          <p className="text-slate-600 mb-6">We don't have a dedicated page for that city yet.</p>
          <Button onClick={() => navigate("/become-dealer")} className="bg-teal hover:bg-teal-dark text-white">
            Apply to Become a Dealer <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const stateSlug = (city.state || "").toLowerCase().replace(/\s+/g, "");
  const fullName = `${city.city}, ${city.state}`;
  const h1 = `Open a Total Wireless Store in ${fullName}`;
  const canonical = `https://primemediawireless.com/cities/${city.slug}`;

  // LocalBusiness JSON-LD scoped to this city
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": canonical,
    name: `Prime Media Wireless — ${fullName}`,
    description: city.meta_description,
    url: canonical,
    areaServed: {
      "@type": "City",
      name: city.city,
      containedInPlace: { "@type": "State", name: city.state },
    },
    parentOrganization: {
      "@type": "Organization",
      name: "Prime Media Wireless",
      url: "https://primemediawireless.com",
    },
    telephone: "+1-248-590-0000",
    email: "info@primemediawireless.com",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://primemediawireless.com/" },
      { "@type": "ListItem", position: 2, name: "Open A Store", item: "https://primemediawireless.com/open-a-store" },
      { "@type": "ListItem", position: 3, name: city.state, item: `https://primemediawireless.com/${stateSlug}` },
      { "@type": "ListItem", position: 4, name: city.city, item: canonical },
    ],
  };

  const faqs = [
    { q: `How do I open a Total Wireless store in ${fullName}?`,
      a: `Submit the dealer application at Prime Media Wireless, qualify via interview, sign the Total Wireless Exclusive Retailer Agreement, receive inventory and signage, then launch your ${city.city} store with hands-on grand-opening support.` },
    { q: `Are there opening incentives for ${city.city} dealers?`,
      a: city.has_incentive
        ? `Yes — opening incentives are currently available for qualified Total Wireless operators in ${fullName}. Contact Prime Media Wireless to learn what's currently offered for your specific market.`
        : `Incentive eligibility varies by market and is reviewed during the qualification interview. Contact Prime Media Wireless for the latest available programs in ${fullName}.` },
    { q: `What does it cost to open a Total Wireless store in ${city.city}?`,
      a: `Typical startup investment ranges $15,000–$40,000 covering inventory, fixtures, signage, point-of-sale, and launch marketing. There is no franchise fee.` },
    { q: `What neighborhoods in ${city.city} are best for a Total Wireless store?`,
      a: `High-foot-traffic retail corridors in ${(city.neighborhoods || []).slice(0, 3).join(", ") || city.city} tend to perform strongest, but Prime Media Wireless will help evaluate your specific location during the qualification process.` },
  ];
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <PublicLayout>
      <SEOHead
        title={city.meta_title || `${h1} | Prime Media Wireless`}
        description={city.meta_description}
        canonical={canonical}
        jsonLd={[localBusinessSchema, breadcrumbSchema, faqSchema]}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-navy via-navy to-slate-900 text-white py-16 sm:py-20" data-testid="city-hero">
        <div className="max-w-5xl mx-auto px-6">
          <motion.nav initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-slate-300 mb-4 font-body">
            <Link to="/" className="hover:text-teal">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/open-a-store" className="hover:text-teal">Open A Store</Link>
            <span className="mx-2">/</span>
            <Link to={`/${stateSlug}`} className="hover:text-teal">{city.state}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{city.city}</span>
          </motion.nav>
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl mb-4 leading-tight"
            data-testid="city-h1"
          >
            {h1}
          </motion.h1>
          <p className="text-slate-200 text-base sm:text-lg max-w-3xl font-body">{city.intro_paragraph}</p>

          {city.has_incentive && (
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="mt-6 inline-flex items-center gap-2 bg-teal/15 border border-teal/40 text-teal-light px-4 py-2 rounded-full text-sm font-semibold"
              data-testid="city-incentive-badge"
            >
              <Sparkles className="w-4 h-4" /> Opening incentives currently available for qualified operators
            </motion.div>
          )}

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/become-dealer#apply" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white px-6 py-3 rounded-full font-semibold transition-colors" data-testid="city-cta-apply">
              Apply to Open a Store in {city.city} <ArrowRight className="w-4 h-4" />
            </Link>
            <BookCallButton city={city.city} state={city.state} />
            <a href="tel:+12485900000" className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-6 py-3 rounded-full font-semibold transition-colors">
              <Phone className="w-4 h-4" /> (248) 590-0000
            </a>
          </div>
        </div>
      </section>

      {/* Why open here */}
      <section className="py-14 bg-white border-b border-slate-100" data-testid="city-why">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-4">Why Open a Total Wireless Store in {city.city}</h2>
          <p className="text-slate-700 font-body text-base leading-relaxed">{city.why_open_here}</p>
          {city.population && (
            <p className="text-slate-500 text-sm mt-4 font-body italic">Market profile: {city.population}</p>
          )}
        </div>
      </section>

      {/* Neighborhoods */}
      {(city.neighborhoods || []).length > 0 && (
        <section className="py-12 bg-slate-50 border-b border-slate-100" data-testid="city-neighborhoods">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-3">{city.city} Neighborhoods We Cover</h2>
            <p className="text-slate-600 font-body mb-5">Prime Media Wireless recruits Total Wireless Exclusive Retailers across {city.city}. Strong retail corridors include:</p>
            <div className="flex flex-wrap gap-2">
              {city.neighborhoods.map((n) => (
                <span key={n} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-navy px-3 py-1.5 rounded-full text-sm font-medium">
                  <MapPin className="w-3.5 h-3.5 text-teal" /> {n}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5-step process — localized */}
      <section className="py-14 bg-white border-b border-slate-100" data-testid="city-process">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-6">How to Become a Total Wireless Dealer in {city.city}</h2>
          <ol className="space-y-3 text-slate-700 font-body text-base">
            {[
              `Apply online at Prime Media Wireless — share your ${city.city} business details and proposed location.`,
              `Qualify via interview — confirm your retail real estate, business license, and capital range.`,
              `Sign the Total Wireless Exclusive Retailer Agreement — protected territory in your ${city.city} market.`,
              `Receive Verizon-grade inventory, branded signage, and POS configured for your ${city.city} store.`,
              `Launch with hands-on grand-opening support from the Prime Media Wireless team.`,
            ].map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-slate-50 border-b border-slate-100" data-testid="city-faq">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-6">FAQs — Total Wireless Stores in {city.city}</h2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="bg-white border border-slate-200 rounded-lg p-4 group">
                <summary className="font-semibold text-navy cursor-pointer flex justify-between items-center">
                  {f.q}
                  <span className="text-teal text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-slate-700 font-body mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities cross-link */}
      {otherCities.length > 0 && (
        <section className="py-12 bg-white border-b border-slate-100" data-testid="city-other-cities">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">Other {city.state} Cities</h2>
            <div className="flex flex-wrap gap-2">
              {otherCities.map((o) => (
                <Link key={o.slug} to={`/cities/${o.slug}`}
                  className="inline-flex items-center gap-1.5 bg-slate-50 hover:bg-teal/10 border border-slate-200 hover:border-teal text-navy px-3 py-2 rounded-full text-sm font-medium transition-colors">
                  <Building2 className="w-3.5 h-3.5 text-teal" /> {o.city}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 bg-navy text-white" data-testid="city-final-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl mb-3">Ready to Open Your {city.city} Total Wireless Store?</h2>
          <p className="text-slate-200 font-body mb-6">Apply now — protected territory and dealer onboarding wait.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/become-dealer#apply" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white px-6 py-3 rounded-full font-semibold">
              Start Your Application <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:info@primemediawireless.com" className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-6 py-3 rounded-full font-semibold">
              <Mail className="w-4 h-4" /> info@primemediawireless.com
            </a>
          </div>
        </div>
      </section>

      {/* Awards + Why Choose PMW — puts the 2026 Growth Champion recognition on every city page */}
      <AwardsSection compact heading={`Why Prime Media Wireless in ${city.city}`} subheading="The 2026 Master Agent Award – Growth Champion is why operators pick us to open their store." />
      <WhyChoosePmw />

      <SEOExpansion stateName={city.state} />
    </PublicLayout>
  );
}
